/**
 * h5/src/ws/secretProvider.ts
 * Secret 提供者抽象（三模式：Env / Tauri / Ticket）
 *
 * R2 红线落地：
 * - EnvSecretProvider：仅 DEV 环境可用，生产构建时抛错
 * - TauriSecretProvider：通过 window.__TAURI__.invoke 从 Rust 侧获取签名
 * - TicketSecretProvider：调 GET /api/kiosk/ws-ticket 拿短期 sig（F.1 真实接通）
 *
 * 工厂函数 getDefaultProvider() 自动按运行环境选择实现。
 */

import { logger } from '@/utils/logger'
import { getEffectiveMachineCodeSync } from '@/utils/machineCode'
import { sign, generateNonce } from './signer'
import type { SecretProvider } from './types'

// ─── R2 编译期守护 ──────────────────────────────────────────────────────────
// 注意：此检查在 EnvSecretProvider 的运行时构造中执行

// ─── 模式 A：EnvSecretProvider（仅 DEV）──────────────────────────────────────

/**
 * 开发模式 Secret 提供者
 *
 * R2 落地：
 * - 若 import.meta.env.DEV !== true，构造时抛错（阻止生产构建使用）
 * - 读取 VITE_KIOSK_WS_HMAC_SECRET（仅存在于 .env.local，gitignored）
 */
export class EnvSecretProvider implements SecretProvider {
  private readonly _secret: string

  constructor() {
    // R2：生产环境严禁使用此 Provider
    if (!import.meta.env.DEV) {
      throw new Error(
        '[R2 Violation] EnvSecretProvider is only allowed in DEV mode. ' +
        'Use TauriSecretProvider or TicketSecretProvider in production.',
      )
    }

    const secret = import.meta.env.VITE_KIOSK_WS_HMAC_SECRET as string | undefined
    if (!secret) {
      throw new Error(
        '[EnvSecretProvider] VITE_KIOSK_WS_HMAC_SECRET is not set. ' +
        'Please add it to .env.local (gitignored).',
      )
    }

    this._secret = secret
    logger.info('[SecretProvider] 使用 EnvSecretProvider（开发模式）', {
      machine_code: getEffectiveMachineCodeSync(),
    })
  }

  async getMachineCode(): Promise<string> {
    return getEffectiveMachineCodeSync()
  }

  async buildAuthQuery(): Promise<string> {
    const machineCode = await this.getMachineCode()
    const ts = Math.floor(Date.now() / 1000)
    const nonce = generateNonce()
    const sig = await sign(machineCode, ts, nonce, this._secret)
    return `machine_code=${encodeURIComponent(machineCode)}&ts=${ts}&nonce=${nonce}&sig=${sig}`
  }
}

// ─── 模式 B：TauriSecretProvider（生产 Tauri）────────────────────────────────

/**
 * Tauri 生产模式 Secret 提供者
 *
 * secret 永不进 webview JS；签名由 Rust 侧 invoke('ws_sign') 完成。
 * 若无 Tauri 环境，自动降级到 TicketSecretProvider。
 *
 * 修复：使用 @tauri-apps/api/core 动态导入，兼容 withGlobalTauri:false（Tauri v2 默认）。
 * ws_sign Rust 命令返回 WsAuthQuery { code, ts, nonce, sig }，非 string。
 */
export class TauriSecretProvider implements SecretProvider {
  async getMachineCode(): Promise<string> {
    try {
      const invoke = await this._getInvoke()
      if (invoke) {
        const code = invoke('get_machine_id') as Promise<string>
        return await code
      }
    } catch (err) {
      logger.warn('[TauriSecretProvider] invoke get_machine_id 失败，降级', {
        error: String(err),
      })
    }
    return getEffectiveMachineCodeSync()
  }

  async buildAuthQuery(): Promise<string> {
    try {
      const invoke = await this._getInvoke()
      if (invoke) {
        const machineCode = await this.getMachineCode()
        // Rust ws_sign 返回 WsAuthQuery { code, ts, nonce, sig }，非 string
        const auth = (await invoke('ws_sign', {
          machineCode,
        })) as { code: string; ts: number; nonce: string; sig: string }
        return `machine_code=${encodeURIComponent(auth.code)}&ts=${auth.ts}&nonce=${auth.nonce}&sig=${auth.sig}`
      }
    } catch (err) {
      logger.warn('[TauriSecretProvider] invoke ws_sign 失败（不降级 Ticket，请检查 KEYT 报名）', {
        error: String(err),
      })
      throw err instanceof Error ? err : new Error(String(err))
    }

    throw new Error('[TauriSecretProvider] ws_sign 不可用：非 Tauri 或 invoke 失败')
  }

  /**
   * 获取 Tauri invoke 函数（兼容 withGlobalTauri:false 的 Tauri v2）
   * 懒加载 @tauri-apps/api/core，不依赖 window.__TAURI__ 全局注入
   */
  private async _getInvoke(): Promise<((cmd: string, args?: Record<string, unknown>) => Promise<unknown>) | null> {
    if (!this._isTauri()) return null
    try {
      const { invoke } = await import('@tauri-apps/api/core')
      return invoke as unknown as (cmd: string, args?: Record<string, unknown>) => Promise<unknown>
    } catch (err) {
      logger.warn('[TauriSecretProvider] @tauri-apps/api/core 导入失败', { error: String(err) })
      return null
    }
  }

  /**
   * 检测 Tauri 环境（支持 v1 window.__TAURI__ 和 v2 window.__TAURI_INTERNALS__）
   */
  private _isTauri(): boolean {
    return typeof window !== 'undefined' && (
      '__TAURI__' in window ||
      '__TAURI_INTERNALS__' in window
    )
  }
}

// ─── 模式 C：TicketSecretProvider（生产 H5）──────────────────────────────────

/**
 * ws-ticket 端点响应 Schema（GET /api/kiosk/ws-ticket）
 * F.1 设计文档 §4.3.2
 */
interface WsTicketResponse {
  ticket: string   // 32 字符 hex，一次性消费
  ts: number       // epoch seconds，与签名同源
  nonce: string    // 随机 hex
  sig: string      // HMAC-SHA256 hex 64 字符
  expires_in: number // TTL（秒），默认 90
  issued_at: number
  server_ts: number  // epoch ms，时钟漂移诊断
}

/**
 * 缓存的 ticket 记录（内存中，不写 localStorage）
 * R2-T3：ticket 不持久化
 */
interface CachedTicket {
  response: WsTicketResponse
  expiresAt: number // ms 时间戳
}

/** ticket 提前刷新时间（过期前 10s 主动刷新，与设计文档一致） */
const TICKET_REFRESH_BUFFER_MS = 10_000

/** 429 退避时长（60s） */
const RATE_LIMIT_BACKOFF_MS = 60_000

/** 指数退避基数 */
const BACKOFF_BASE_MS = 1_000

/** 最大指数退避次数 */
const MAX_BACKOFF_RETRIES = 5

/**
 * 生产 H5 模式 Secret 提供者（F.1 真实接通版）
 *
 * R5 落地：通过 useApi composable（不裸 axios）
 * R2 落地：ticket 仅存内存，不写 localStorage；master_secret 不在前端
 * R3 落地：日志不输出完整 ticket
 *
 * 错误处理：
 * - 429 → 退避 60s 后重试一次
 * - 4xx → FAILED（不可恢复）
 * - 5xx / 网络错误 → 指数退避重试（最多 5 次）
 */
export class TicketSecretProvider implements SecretProvider {
  private _machineCode: string
  private _cachedTicket: CachedTicket | null = null
  private _disposed = false

  constructor(machineCode?: string) {
    this._machineCode = machineCode?.trim() || getEffectiveMachineCodeSync()
    logger.info('[TicketSecretProvider] 初始化', { machine_code: this._machineCode })
  }

  async getMachineCode(): Promise<string> {
    const current = getEffectiveMachineCodeSync()
    if (current !== this._machineCode) {
      logger.info('[TicketSecretProvider] machine_code 变更，清除 ticket 缓存', {
        from: this._machineCode,
        to: current,
      })
      this._machineCode = current
      this._cachedTicket = null
    }
    return this._machineCode
  }

  /**
   * 构建 WebSocket 握手 query string（包含 ticket 字段）
   *
   * 格式：code=XXX&ts=NNN&nonce=HHH&sig=HHH&ticket=HHH
   * 后端双轨鉴权（A.4）识别 ticket 字段走 ticket 模式
   */
  async buildAuthQuery(): Promise<string> {
    if (this._disposed) {
      throw new Error('[TicketSecretProvider] Provider 已 dispose，不可再用')
    }

    await this.getMachineCode()
    const ticket = await this._getOrFetchTicket()

    // 构建完整 query（包含 ticket 字段，与 A.4 双轨鉴权对齐）
    const params = new URLSearchParams({
      machine_code: this._machineCode,
      ts: String(ticket.ts),
      nonce: ticket.nonce,
      sig: ticket.sig,
      ticket: ticket.ticket,
    })

    return params.toString()
  }

  /**
   * 释放缓存（可选调用，在组件销毁时调用）
   * R2-T3：清除内存中的 ticket
   */
  dispose(): void {
    this._cachedTicket = null
    this._disposed = true
    logger.info('[TicketSecretProvider] disposed', { machine_code: this._machineCode })
  }

  // ── 私有方法 ──────────────────────────────────────────────────────────────

  /**
   * 获取有效 ticket（优先复用缓存，TTL 剩余 ≤10s 则主动刷新）
   */
  private async _getOrFetchTicket(): Promise<WsTicketResponse> {
    const now = Date.now()

    // 检查缓存是否有效（TTL 剩余 > TICKET_REFRESH_BUFFER_MS）
    if (
      this._cachedTicket !== null &&
      this._cachedTicket.expiresAt - TICKET_REFRESH_BUFFER_MS > now
    ) {
      logger.info('[TicketSecretProvider] 使用缓存 ticket', {
        machine_code: this._machineCode,
        ticket_preview: this._cachedTicket.response.ticket.substring(0, 8) + '...',
        remaining_ms: this._cachedTicket.expiresAt - now,
      })
      return this._cachedTicket.response
    }

    // 需要刷新
    logger.info('[TicketSecretProvider] 获取新 ticket', { machine_code: this._machineCode })
    const ticket = await this._fetchTicketWithRetry()

    // 缓存（R2-T3：仅内存，不写 localStorage）
    this._cachedTicket = {
      response: ticket,
      expiresAt: now + ticket.expires_in * 1000,
    }

    return ticket
  }

  /**
   * 带重试逻辑的 ticket 获取
   *
   * - 429 → 退避 60s 后重试一次
   * - 4xx → 直接抛错（不可恢复）
   * - 5xx / 网络错误 → 指数退避（1s, 2s, 4s, 8s, 16s）
   */
  private async _fetchTicketWithRetry(): Promise<WsTicketResponse> {
    let lastError: Error | null = null

    for (let attempt = 0; attempt <= MAX_BACKOFF_RETRIES; attempt++) {
      try {
        return await this._fetchTicket()
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        lastError = error

        const statusCode = this._extractStatusCode(error)

        // 429：退避 60s 后重试一次
        if (statusCode === 429) {
          if (attempt === 0) {
            logger.warn('[TicketSecretProvider] 429 限流，退避 60s 后重试', {
              machine_code: this._machineCode,
            })
            await this._sleep(RATE_LIMIT_BACKOFF_MS)
            continue // 重试
          } else {
            throw new Error(`[TicketSecretProvider] 429 限流重试后仍失败: ${error.message}`)
          }
        }

        // 4xx（非 429）：不可恢复，直接抛错
        if (statusCode !== null && statusCode >= 400 && statusCode < 500) {
          logger.error('[TicketSecretProvider] 4xx 错误（不可恢复）', {
            machine_code: this._machineCode,
            status: statusCode,
          })
          throw new Error(`[TicketSecretProvider] 不可恢复错误 HTTP ${statusCode}: ${error.message}`)
        }

        // 5xx / 网络错误：指数退避
        if (attempt < MAX_BACKOFF_RETRIES) {
          const backoffMs = BACKOFF_BASE_MS * Math.pow(2, attempt)
          logger.warn('[TicketSecretProvider] 请求失败，指数退避', {
            machine_code: this._machineCode,
            attempt: attempt + 1,
            backoff_ms: backoffMs,
            error: error.message,
          })
          await this._sleep(backoffMs)
        }
      }
    }

    throw new Error(`[TicketSecretProvider] 超过最大重试次数: ${lastError?.message}`)
  }

  /**
   * 实际调用 GET /api/kiosk/ws-ticket
   *
   * R5 落地：使用 useApi composable（不裸 axios）
   * 注意：useApi 是 composable，在 Vue setup 外使用需通过动态导入
   */
  private async _fetchTicket(): Promise<WsTicketResponse> {
    // R5：动态导入 useApi（避免 composable 在 class 构造时绑定 Vue 实例）
    const { useApi, ApiError } = await import('@/composables/useApi')
    const api = useApi()

    const url = `/api/kiosk/ws-ticket?machine_code=${encodeURIComponent(this._machineCode)}`
    logger.info('[TicketSecretProvider] 请求 ws-ticket', {
      machine_code: this._machineCode,
      url,
    })

    try {
      // R5：通过 useApi().get() 而非裸 fetch/axios
      // ApiResponse<T> = { code: number, message: string, data: T }
      const response = await api.get<WsTicketResponse>(url)
      // useApi().get() 返回 ApiResponse<T>，data 字段就是实际数据
      const ticketData: WsTicketResponse = response.data

      if (!ticketData.ticket || !ticketData.ts || !ticketData.nonce || !ticketData.sig) {
        throw new Error('ws-ticket 响应格式错误：缺少必要字段')
      }

      // R3：日志只显示 ticket 前 8 字符
      logger.info('[TicketSecretProvider] ticket 获取成功', {
        machine_code: this._machineCode,
        ticket_preview: ticketData.ticket.substring(0, 8) + '...',
        expires_in: ticketData.expires_in,
      })

      return ticketData
    } catch (err) {
      if (err instanceof ApiError) {
        // 捕获机台未注册等业务错误并反馈给 pushStore 供 UI 展示
        import('@/stores/pushStore').then(({ usePushStore }) => {
          const pushStore = usePushStore()
          pushStore.recordError(err.message, err.errorCode)
        }).catch(() => {})

        // ApiError 携带状态码，方便重试逻辑判断
        const statusError = new Error(`HTTP ${err.statusCode}: ${err.message}`)
        ;(statusError as Error & { statusCode?: number }).statusCode = err.statusCode
        throw statusError
      }
      throw err
    }
  }

  /**
   * 从错误消息中提取 HTTP 状态码
   */
  private _extractStatusCode(error: Error): number | null {
    const statusCode = (error as Error & { statusCode?: number }).statusCode
    if (statusCode !== undefined) return statusCode

    // 从消息中解析 "HTTP NNN:" 格式
    const match = error.message.match(/HTTP\s+(\d{3})/)
    if (match?.[1]) return parseInt(match[1], 10)

    return null
  }

  /**
   * sleep 工具（可被测试 mock）
   */
  private _sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}

// ─── 工厂函数 ────────────────────────────────────────────────────────────────

/**
 * 根据运行环境自动选择合适的 SecretProvider
 *
 * 优先级：
 * 1. DEV 模式 + VITE_KIOSK_WS_HMAC_SECRET 存在 → EnvSecretProvider
 * 2. Tauri 环境（window.__TAURI__ 存在）→ TauriSecretProvider
 * 3. 纯 H5（生产）→ TicketSecretProvider（F.1 真实接通）
 *
 * 注：DEV 模式若无 VITE_KIOSK_WS_HMAC_SECRET，也走 TicketSecretProvider
 *     方便在 dev 服务器联调真实后端
 */
export function getDefaultProvider(): SecretProvider {
  const isDev = import.meta.env.DEV === true
  const hasSecret = Boolean(import.meta.env.VITE_KIOSK_WS_HMAC_SECRET)

  // DEV + 有明文 secret → EnvSecretProvider（纯前端开发）
  if (isDev && hasSecret) {
    try {
      return new EnvSecretProvider()
    } catch (err) {
      logger.warn('[SecretProvider] EnvSecretProvider 初始化失败，降级', { error: String(err) })
    }
  }

  // Tauri 环境 → TauriSecretProvider
  // 兼容 Tauri v1（window.__TAURI__）和 v2（window.__TAURI_INTERNALS__，withGlobalTauri:false 时仍存在）
  const hasTauri = typeof window !== 'undefined' && (
    '__TAURI__' in window ||
    '__TAURI_INTERNALS__' in window
  )
  if (hasTauri) {
    logger.info('[SecretProvider] 使用 TauriSecretProvider')
    return new TauriSecretProvider()
  }

  // 纯 H5（含 DEV 联调真实后端）→ TicketSecretProvider（F.1）
  logger.info('[SecretProvider] 使用 TicketSecretProvider（纯 H5 模式）')
  return new TicketSecretProvider()
}
